/* ============================================================
   Konfigurátor darčekového balenia — Variant B (Editorial)
   State machine + Shopify AJAX integration
   
   Architecture:
     - State held in a single object, persisted to sessionStorage
     - Products fetched from collection JSON endpoints
     - Add-to-cart via /cart/add.js with line item properties
       (_bundle_id groups items, _bundle_label sets display name)
   ============================================================ */

(function() {
  'use strict';

  // ---------- Config ----------
  const STORAGE_KEY = 'gc_state_v1';
  const STEPS = ['drink', 'deli', 'accessories', 'pack', 'wish', 'summary'];
  const STEP_LABELS = ['Nápoj', 'Delikatesy', 'Doplnky', 'Balenie', 'Prianie', 'Zhrnutie'];
  
  // Size class mapping: drives packaging filter
  const SIZE_RANK = { 'small': 1, 'medium': 2, 'large': 3, 'xlarge': 4 };
  
  // Wish card templates (pre-canned phrases)
  const TEMPLATES = {
    'minimal': { tagline: '', text: '' },
    'narodeniny': { tagline: 'Všetko najlepšie', text: 'K Tvojim narodeninám prajem všetko dobré, veľa zdravia a radosti.' },
    'vyrocie': { tagline: 'K výročiu', text: 'Ďakujeme za všetky spoločné chvíle.' },
    'vianoce': { tagline: 'Veselé Vianoce', text: 'Pokojné sviatky a šťastný nový rok.' }
  };

  // ---------- State ----------
  const initialState = () => ({
    step: 0,
    maxStepReached: 0, // furthest step the user has navigated to — drives clickable breadcrumb dots
    editMode: false,   // summary's "Niečo upraviť" toggle — when true, × buttons appear on rows
    drinkType: 'vino',
    selections: {
      drink: [],     // product objects with selected variant
      deli: [],
      accessories: [],
      pack: null,
      wish: { template: 'minimal', to: '', from: '', text: '', tagline: '' }
    },
    // Per-step filter (the leaf-label "variety" chip). Default 'all'.
    filters: { drink: 'all', deli: 'all', accessories: 'all' },
    // Optional second-axis facet — e.g. wine COLOR for the drink step.
    // Compose with `filters` via AND when present.
    facets: { drink: 'all' }
  });

  let state = loadState() || initialState();

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function saveState() {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }

  // ---------- Init ----------
  const root = document.querySelector('.gc');
  if (!root) return;
  const currency = root.dataset.currency || '€';
  const bundleHandle = root.dataset.bundleProductHandle || '';

  // Cache for collection fetches
  const collectionCache = new Map();

  async function fetchCollection(handle) {
    if (collectionCache.has(handle)) return collectionCache.get(handle);
    try {
      const res = await fetch(`/collections/${handle}/products.json?limit=50`);
      const data = await res.json();
      // Drop products with no available variant — otherwise a customer
      // can pick a sold-out item and /cart/add.js rejects the whole
      // bundle because Shopify's bulk-add is atomic. Easier to never
      // surface unavailable inventory in the first place.
      const products = (data.products || [])
        .map(normalizeProduct)
        .filter((p) => p.available);
      collectionCache.set(handle, products);
      return products;
    } catch (e) {
      console.error('[GC] collection fetch failed', handle, e);
      return [];
    }
  }

  function normalizeProduct(p) {
    // Extract metafields if present (Shopify products.json doesn't expose them
    // by default — assume they come via product tags or product_type fallbacks)
    // Pick the FIRST AVAILABLE variant; some products have multiple variants
    // where the first is sold out but a later one isn't.
    const variants = p.variants || [];
    const v = variants.find((x) => x.available !== false) || variants[0] || {};
    const tags = (p.tags || []).map(t => t.toLowerCase());
    
    // Heuristic role detection from tags (gift_role:drink, gift_role:packaging, etc.)
    let role = 'drink';
    let sizeClass = 'medium';
    let drinkType = null;
    let category = null;
    
    tags.forEach(tag => {
      if (tag.startsWith('gift_role:')) role = tag.split(':')[1];
      if (tag.startsWith('size:')) sizeClass = tag.split(':')[1];
      if (tag.startsWith('drink:')) drinkType = tag.split(':')[1];
      if (tag.startsWith('cat:')) category = tag.split(':')[1];
    });

    // Fallback: use Shopify's native product_type as the category. This
    // is what powers the merchant's storefront menu (Orechy / Cestoviny
    // / Omáčky / Olej / Sladké / Slané / Káva …) so the configurator
    // filter chips automatically mirror the catalog structure without
    // needing extra tagging.
    if (!category && p.product_type) category = p.product_type.trim();
    
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      vendor: p.vendor || '',
      productType: p.product_type || '',
      price: parseFloat(v.price || '0'),
      variantId: v.id,
      available: v.available !== false,
      image: (p.images && p.images[0]) ? p.images[0].src : '',
      tags,
      role,
      sizeClass,
      drinkType,
      category
    };
  }

  // ---------- Renderers ----------
  
  function renderProgress() {
    const dots = root.querySelectorAll('.gc-prog-dot');
    const maxReachable = Math.max(state.step, state.maxStepReached || 0);
    dots.forEach((d, i) => {
      if (i < state.step) d.dataset.on = 'done';
      else if (i === state.step) d.dataset.on = 'active';
      else d.removeAttribute('data-on');
      // Reachable dots are visually clickable (cursor + hover style),
      // unreachable ones are dimmed and cursor:not-allowed.
      d.dataset.reachable = i <= maxReachable ? '1' : '0';
    });
  }

  function renderStep() {
    root.querySelectorAll('.gc-step').forEach(el => {
      const isActive = parseInt(el.dataset.step, 10) === state.step;
      el.hidden = !isActive;
      if (isActive) el.dataset.stepActive = '';
      else el.removeAttribute('data-step-active');
    });
    renderProgress();
    renderFooter();
    renderDrawer();
    
    // Trigger step-specific render
    const stepName = STEPS[state.step];
    if (stepName === 'drink') renderProductStep('drink', state.drinkType === 'vino' ? 'vino' : 'destilaty-a-liehoviny', 0);
    if (stepName === 'deli') renderProductStep('deli', 'delikatesy', 1);
    if (stepName === 'accessories') renderProductStep('accessories', 'doplnky', 2);
    if (stepName === 'pack') renderPackStep();
    if (stepName === 'wish') renderWishStep();
    if (stepName === 'summary') renderSummary();
  }

  async function renderProductStep(key, collectionHandle, stepIdx) {
    const stepEl = root.querySelector(`.gc-step[data-step="${stepIdx}"]`);
    const host = stepEl.querySelector('[data-products-host]');
    const filterHost = stepEl.querySelector('[data-filter-host]');
    
    host.innerHTML = '<div class="gc-loading">Načítavam produkty…</div>';
    const products = await fetchCollection(collectionHandle);
    
    if (!products.length) {
      host.innerHTML = '<div class="gc-empty">Žiadne produkty v tejto kolekcii.</div>';
      return;
    }
    
    // Build filter chips. Display rules:
    //   - Strip the parent path: 'Víno > Červené víno > Merlot' → 'Merlot'.
    //   - On the drink step we render a SECOND row of facet chips for
    //     wine color (Červené / Biele / Ružové / Šumivé). Color is
    //     extracted from the second segment of the product_type path,
    //     so 'Víno > Červené víno > X' → 'Červené víno'. Filtering
    //     composes color × leaf via AND.
    if (filterHost) {
      const leafLabel = (cat) => {
        if (!cat) return '';
        const parts = String(cat).split('>').map((s) => s.trim()).filter(Boolean);
        return parts[parts.length - 1] || cat;
      };
      const colorLabel = (cat) => {
        if (!cat) return '';
        const parts = String(cat).split('>').map((s) => s.trim()).filter(Boolean);
        // expects 'Víno > Červené víno > <leaf>'  — return the second segment
        return parts.length >= 2 ? parts[1] : '';
      };
      const topLabel = (cat) => {
        if (!cat) return '';
        const parts = String(cat).split('>').map((s) => s.trim()).filter(Boolean);
        return parts[0] || '';
      };

      const isWineStep = key === 'drink' && state.drinkType === 'vino';

      // Expected top-level product_type for the step. Products in the gift
      // collections sometimes have wrong product_types (e.g. a wine bottle
      // tagged into 'gift-delikatesy' with product_type 'Víno > Ružové víno')
      // and would pollute the chip strip with irrelevant leaves like 'Ružové
      // víno' under Delikatesy. We restrict chip generation to products whose
      // top-level segment matches the step's category, so the strip shows
      // only meaningful filters even when the catalog is messy.
      const TOP_LEVEL_FOR_STEP = {
        drink: state.drinkType === 'vino' ? 'Víno' : 'Destiláty',
        deli: 'Delikatesy',
        accessories: 'Doplnky',
      };
      const expectedTop = TOP_LEVEL_FOR_STEP[key] || '';
      // Prefix-match (mirrors Shopify smart collection's `TYPE STARTS_WITH`):
      // 'Destiláty' should match 'Destiláty a liehoviny > Rum' but not
      // 'Vínoteca'. Boundary char must be space, '>' or end-of-string.
      const matchesExpectedTopChip = (cat) => {
        if (!expectedTop) return true;
        if (!cat) return false;
        const s = String(cat);
        if (!s.startsWith(expectedTop)) return false;
        const n = s.charAt(expectedTop.length);
        return n === '' || n === ' ' || n === '>';
      };

      // Active facet (color) for the wine drink step ONLY. Destilát
      // step has no color facet — left over state.facets.drink (e.g.
      // 'Biele víno' set on the wine tab) must not leak across when the
      // user switches to destilát, otherwise color filter would zero out
      // every distillate.
      const activeFacet = isWineStep ? (state.facets?.drink || 'all') : 'all';

      // Build color chip set. Source of truth is the site's `vino`
      // linklist — same submenu the storefront's main nav uses, so the
      // configurator stays in sync with merchant menu (Červené / Biele /
      // Šumivé / …) and the variety drill-down (Cabernet under Červené,
      // Chardonnay under Biele, etc.) mirrors what's in the storefront.
      let wineMenu = [];
      if (isWineStep) {
        try {
          const menuEl = root.querySelector('script[data-vino-menu]');
          if (menuEl) wineMenu = JSON.parse(menuEl.textContent || '[]') || [];
        } catch (e) { /* ignore — fall through */ }
        // Fallback: synthesize menu from product_type values if linklist missing
        if (wineMenu.length === 0) {
          const colors = new Set();
          for (const p of products) {
            const c = colorLabel(p.category);
            if (c && /víno|vino/i.test(c)) colors.add(c);
          }
          wineMenu = Array.from(colors).map((title) => ({ title, varieties: [] }));
        }
      }
      let facetRowHtml = '';
      if (isWineStep && wineMenu.length > 0) {
        const sortedColors = wineMenu.map((c) => c.title).filter(Boolean).slice()
          .sort((a, b) => a.localeCompare(b, 'sk'));
        facetRowHtml = `
          <div class="gc-facet-row">
            <button type="button" class="gc-filter gc-filter-facet ${activeFacet === 'all' ? 'gc-filter-on' : ''}" data-facet="all">Všetky farby</button>
            ${sortedColors.map((c) => `<button type="button" class="gc-filter gc-filter-facet ${activeFacet === c ? 'gc-filter-on' : ''}" data-facet="${escapeHtml(c)}">${escapeHtml(c)}</button>`).join('')}
          </div>
        `;
      }

      // Build leaf-label chip set, possibly narrowed by active facet.
      // Build variety/sub-category chip set. Two strategies:
      //  - Wine step + color picked: pull varieties from the linklist menu
      //    (Cabernet / Merlot under Červené, Chardonnay / Riesling under
      //    Biele, etc.) — mirrors the storefront's main nav exactly.
      //  - All other cases: fall back to leaves derived from product_type
      //    so non-wine steps still get a useful drill-down row.
      // Wine step with no color picked → suppress (unified hierarchy).
      const facetMatchedProducts = (isWineStep && activeFacet !== 'all')
        ? products.filter((p) => colorLabel(p.category) === activeFacet)
        : products;
      const suppressLeafRow = isWineStep && activeFacet === 'all';
      let sortedLabels = [];
      if (!suppressLeafRow) {
        if (isWineStep && activeFacet !== 'all') {
          // Source: menu varieties under the active color.
          // The Shopify nav often includes a self-link as the first nested
          // child (e.g. 'Biele víno → Biele víno' = "all whites"), so we
          // strip out any variety whose title matches a color label.
          const colorTitles = new Set(wineMenu.map((c) => c.title).filter(Boolean));
          const colorEntry = wineMenu.find((c) => c.title === activeFacet);
          const menuVarieties = (colorEntry?.varieties || [])
            .map((v) => (v.title || '').trim())
            .filter((t) => t && !colorTitles.has(t));
          const productLeaves = new Set(
            facetMatchedProducts
              .filter((p) => matchesExpectedTopChip(p.category))
              .filter((p) => String(p.category).split('>').filter((s) => s.trim()).length > 1)
              .map((p) => leafLabel(p.category))
              .filter((l) => l && !colorTitles.has(l))
          );
          const seen = new Set();
          sortedLabels = menuVarieties.filter((v) => {
            if (seen.has(v)) return false;
            seen.add(v);
            return productLeaves.has(v);
          });
          // If the menu yields nothing matching the catalog, fall back to leaves
          if (sortedLabels.length === 0) {
            sortedLabels = Array.from(productLeaves).sort((a, b) => a.localeCompare(b, 'sk'));
          }
        } else {
          // Non-wine step: prefer the storefront menu (delikatesy / doplnky
          // linklists) as the canonical sub-category source. Falls back to
          // product_type leaves if menu missing or yields nothing.
          const MENU_SELECTOR = { deli: 'script[data-deli-menu]', accessories: 'script[data-acc-menu]' };
          let menuLabels = [];
          const sel = MENU_SELECTOR[key];
          if (sel) {
            try {
              const el = root.querySelector(sel);
              if (el) {
                const items = JSON.parse(el.textContent || '[]') || [];
                menuLabels = items.map((it) => (it.title || '').trim()).filter(Boolean);
              }
            } catch (e) { /* ignore */ }
          }
          // Build set of leaves actually present in this collection (filtered
          // by expected top-level so chips can't surface miscategorized rows).
          // Skip products with single-segment product_type — leafLabel would
          // equal topLabel ('Destiláty a liehoviny' → 'Destiláty a liehoviny')
          // and surface as a redundant chip identical to the step's category.
          const productLeaves = new Set(
            facetMatchedProducts
              .filter((p) => p.category)
              .filter((p) => matchesExpectedTopChip(p.category))
              .filter((p) => String(p.category).split('>').filter((s) => s.trim()).length > 1)
              .map((p) => leafLabel(p.category))
          );
          if (menuLabels.length > 0) {
            const seen = new Set();
            sortedLabels = menuLabels.filter((v) => {
              if (seen.has(v)) return false;
              seen.add(v);
              return productLeaves.has(v);
            });
          }
          if (sortedLabels.length === 0) {
            // Fallback: leaves from product_type
            sortedLabels = Array.from(productLeaves).sort((a, b) => a.localeCompare(b, 'sk'));
          }
        }
      }
      const activeFilter = state.filters[key] || 'all';

      // Variety row: NO 'Všetko' chip — redundant with the row-1 'Všetky
      // farby' reset on the wine step, and on non-wine steps the user can
      // just click the active chip again to toggle off (handled below).
      filterHost.innerHTML = facetRowHtml + `
        <div class="gc-facet-row">
          ${sortedLabels.map((lbl) => {
            const isActive = activeFilter === lbl;
            return `<button type="button" class="gc-filter ${isActive ? 'gc-filter-on' : ''}" data-filter="${escapeHtml(lbl)}">${escapeHtml(lbl)}</button>`;
          }).join('')}
        </div>
      `;
      filterHost.querySelectorAll('[data-filter]').forEach(btn => {
        btn.addEventListener('click', () => {
          // Click an already-active chip to toggle the filter off
          const next = state.filters[key] === btn.dataset.filter ? 'all' : btn.dataset.filter;
          state.filters[key] = next;
          saveState();
          renderProductStep(key, collectionHandle, stepIdx);
        });
      });
      filterHost.querySelectorAll('[data-facet]').forEach(btn => {
        btn.addEventListener('click', () => {
          state.facets = state.facets || {};
          state.facets.drink = btn.dataset.facet;
          // Reset variety filter when changing facet so the user starts fresh
          state.filters.drink = 'all';
          saveState();
          renderProductStep(key, collectionHandle, stepIdx);
        });
      });
    }
    
    // Filter products. activeFilter = leaf label (e.g. 'Merlot');
    // activeFacet = color group on the drink step (e.g. 'Červené víno').
    // Compose color × variety with AND.
    const leafLabel = (cat) => {
      if (!cat) return '';
      const parts = String(cat).split('>').map((s) => s.trim()).filter(Boolean);
      return parts[parts.length - 1] || cat;
    };
    const colorLabel = (cat) => {
      if (!cat) return '';
      const parts = String(cat).split('>').map((s) => s.trim()).filter(Boolean);
      return parts.length >= 2 ? parts[1] : '';
    };
    // Each step expects products of a specific top-level product_type.
    // We prefix-match the full category (e.g. expectedTop='Destiláty'
    // matches 'Destiláty a liehoviny > Rum'). This mirrors the Shopify
    // smart collection rule `TYPE STARTS_WITH <prefix>` so the configurator
    // and the canonical collection stay in agreement.
    const TOP_LEVEL_FOR_CARDS = {
      drink: state.drinkType === 'vino' ? 'Víno' : 'Destiláty',
      deli: 'Delikatesy',
      accessories: 'Doplnky',
    };
    const expectedTopCard = TOP_LEVEL_FOR_CARDS[key] || '';
    const matchesExpectedTop = (cat) => {
      if (!expectedTopCard) return true;
      if (!cat) return false;
      // Prefix match on the first segment, allowing 'Destiláty' to match
      // 'Destiláty a liehoviny > Rum' and 'Víno' to match 'Víno > X' but
      // NOT 'Vínoteca' (must be followed by space, '>' or end-of-string).
      const s = String(cat);
      if (!s.startsWith(expectedTopCard)) return false;
      const next = s.charAt(expectedTopCard.length);
      return next === '' || next === ' ' || next === '>';
    };
    const activeFilter = state.filters[key] || 'all';
    // Same isWineStep gating for the card filter — destilát must not
    // inherit a wine-color facet that the user picked in the previous tab.
    const activeFacet = (key === 'drink' && state.drinkType === 'vino')
      ? (state.facets?.drink || 'all')
      : 'all';
    const filtered = products.filter((p) => {
      const matchTop = matchesExpectedTop(p.category);
      const matchVar = activeFilter === 'all' || leafLabel(p.category) === activeFilter;
      const matchColor = activeFacet === 'all' || colorLabel(p.category) === activeFacet;
      return matchTop && matchVar && matchColor;
    });
    
    const selectedIds = new Set(state.selections[key].map(p => p.id));

    // Cap visible products to 6 per step so the page isn't overwhelming.
    // Sort order: already-selected first (so the user always sees their
    // picks), then alphabetical. Click "Zobraziť viac" to reveal the rest.
    const PRODUCT_CAP = 6;
    const sorted = filtered.slice().sort((a, b) => {
      const aSel = selectedIds.has(a.id) ? 0 : 1;
      const bSel = selectedIds.has(b.id) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return (a.title || '').localeCompare(b.title || '', 'sk');
    });
    state.showAll = state.showAll || {};
    const expanded = !!state.showAll[key];
    const visibleProducts = expanded ? sorted : sorted.slice(0, PRODUCT_CAP);
    const hiddenCount = sorted.length - visibleProducts.length;

    host.innerHTML = visibleProducts.map(p => `
      <button type="button" class="gc-hcard" data-product-id="${p.id}" ${selectedIds.has(p.id) ? 'data-selected' : ''}>
        <img class="gc-hcard-img" src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy"/>
        <div class="gc-hcard-meta">
          ${p.vendor ? `<div class="gc-hcard-eyebrow">${escapeHtml(p.vendor)}</div>` : ''}
          <h3 class="gc-hcard-title">${escapeHtml(p.title)}</h3>
          ${p.category ? `<div class="gc-hcard-tags"><span>${escapeHtml(leafLabel(p.category))}</span></div>` : ''}
        </div>
        <div class="gc-hcard-price">
          <span>${formatPrice(p.price)}</span>
          <span class="gc-hcard-check" aria-hidden="true"></span>
        </div>
      </button>
    `).join('') + (hiddenCount > 0
      ? `<button type="button" class="gc-show-more" data-show-more>Zobraziť viac (${hiddenCount})</button>`
      : (expanded && sorted.length > PRODUCT_CAP
        ? `<button type="button" class="gc-show-more gc-show-less" data-show-more>Zobraziť menej</button>`
        : ''));

    const showMore = host.querySelector('[data-show-more]');
    if (showMore) {
      showMore.addEventListener('click', () => {
        state.showAll[key] = !state.showAll[key];
        saveState();
        renderProductStep(key, collectionHandle, stepIdx);
      });
    }
    
    host.querySelectorAll('.gc-hcard').forEach(card => {
      card.addEventListener('click', () => {
        const id = parseInt(card.dataset.productId, 10);
        const product = filtered.find(p => p.id === id);
        toggleSelection(key, product);
        renderProductStep(key, collectionHandle, stepIdx);
        renderFooter();
      });
    });
  }

  function toggleSelection(key, product) {
    const arr = state.selections[key];
    const idx = arr.findIndex(p => p.id === product.id);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push(product);
    saveState();
  }

  // ---- Step 4: packaging ----
  function computeSizeClass() {
    const items = [
      ...state.selections.drink,
      ...state.selections.deli,
      ...state.selections.accessories
    ];
    if (items.length === 0) return 'small';
    const maxRank = items.reduce((max, p) => Math.max(max, SIZE_RANK[p.sizeClass] || 2), 0);
    // Bump up by item count
    const countBump = items.length >= 6 ? 2 : items.length >= 4 ? 1 : 0;
    const finalRank = Math.min(4, maxRank + countBump);
    return Object.keys(SIZE_RANK).find(k => SIZE_RANK[k] === finalRank) || 'medium';
  }

  function estimatedWeight() {
    // Rough estimate: drink 1.2kg, deli 0.4kg, accessory 0.3kg
    const w =
      state.selections.drink.length * 1.2 +
      state.selections.deli.length * 0.4 +
      state.selections.accessories.length * 0.3;
    return w.toFixed(1);
  }

  async function renderPackStep() {
    const stepEl = root.querySelector('.gc-step[data-step="3"]');
    const host = stepEl.querySelector('[data-packs-host]');
    const meas = stepEl.querySelector('[data-meas-host]');
    
    const itemCount = state.selections.drink.length + state.selections.deli.length + state.selections.accessories.length;
    const sizeClass = computeSizeClass();
    
    meas.querySelector('[data-meas-count]').textContent = itemCount;
    meas.querySelector('[data-meas-weight]').textContent = `~ ${estimatedWeight()} kg`;
    meas.querySelector('[data-meas-size]').textContent = capitalize(sizeClass);
    
    host.innerHTML = '<div class="gc-loading">Načítavam balenia…</div>';
    const packs = await fetchCollection('gift-balenia');
    
    if (!packs.length) {
      host.innerHTML = '<div class="gc-empty">Žiadne balenia.</div>';
      return;
    }
    
    const minRank = SIZE_RANK[sizeClass];
    const selectedId = state.selections.pack ? state.selections.pack.id : null;
    
    host.innerHTML = `<div class="gc-packs">${packs.map(p => {
      const fits = (SIZE_RANK[p.sizeClass] || 2) >= minRank;
      return `
        <button type="button" class="gc-pack" 
          data-product-id="${p.id}"
          ${!fits ? 'data-disabled' : ''}
          ${selectedId === p.id ? 'data-selected' : ''}>
          <img class="gc-pack-img" src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy"/>
          <div>
            <h3 class="gc-pack-title">${escapeHtml(p.title)}</h3>
            <p class="gc-pack-desc">${capitalize(p.sizeClass)} · ${p.productType || 'Balenie'}</p>
            <div class="gc-pack-price">${formatPrice(p.price)}</div>
          </div>
          ${!fits ? '<span class="gc-pack-flag">Príliš malé</span>' : ''}
        </button>
      `;
    }).join('')}</div>`;
    
    host.querySelectorAll('.gc-pack').forEach(card => {
      if (card.hasAttribute('data-disabled')) return;
      card.addEventListener('click', () => {
        const id = parseInt(card.dataset.productId, 10);
        state.selections.pack = packs.find(p => p.id === id);
        saveState();
        renderPackStep();
        renderFooter();
      });
    });
  }

  // ---- Step 5: wish ----
  function renderWishStep() {
    const stepEl = root.querySelector('.gc-step[data-step="4"]');
    const wish = state.selections.wish;
    
    // Templates
    stepEl.querySelectorAll('.gc-tpl-mini').forEach(btn => {
      btn.classList.toggle('gc-tpl-on', btn.dataset.tpl === wish.template);
      btn.onclick = () => {
        wish.template = btn.dataset.tpl;
        const t = TEMPLATES[wish.template] || { tagline: '', text: '' };
        wish.tagline = t.tagline;
        wish.text = t.text;
        saveState();
        renderWishStep();
      };
    });
    
    // Field bindings
    stepEl.querySelectorAll('[data-field]').forEach(input => {
      const field = input.dataset.field;
      input.value = wish[field] || '';
      input.oninput = () => {
        wish[field] = input.value;
        saveState();
        updateCardPreview();
      };
    });
    
    updateCardPreview();
  }

  function updateCardPreview() {
    const wish = state.selections.wish;
    const stepEl = root.querySelector('.gc-step[data-step="4"]');
    stepEl.querySelector('[data-card-to]').textContent = wish.to || '____';
    stepEl.querySelector('[data-card-from]').textContent = wish.from || '____';
    stepEl.querySelector('[data-card-text]').textContent = wish.text || 'Vaše slová tu…';
    stepEl.querySelector('[data-card-tagline]').textContent = wish.tagline || (TEMPLATES[wish.template]?.tagline || '');
    stepEl.querySelector('[data-text-count]').textContent = (wish.text || '').length;
  }

  // ---- Step 6: summary ----
  function getAllItems() {
    return [
      ...state.selections.drink.map(p => ({ ...p, _role: 'drink' })),
      ...state.selections.deli.map(p => ({ ...p, _role: 'deli' })),
      ...state.selections.accessories.map(p => ({ ...p, _role: 'accessories' })),
      ...(state.selections.pack ? [{ ...state.selections.pack, _role: 'pack' }] : [])
    ];
  }

  function getTotalPrice() {
    return getAllItems().reduce((sum, p) => sum + p.price, 0);
  }

  function renderSummary() {
    const stepEl = root.querySelector('.gc-step[data-step="5"]');
    const items = getAllItems();
    const total = getTotalPrice();
    
    stepEl.querySelector('[data-summary-total]').textContent = formatPrice(total);
    stepEl.querySelector('[data-summary-cta-price]').textContent = formatPrice(total);
    
    // Stack visual
    const stack = stepEl.querySelector('[data-summary-stack]');
    stack.innerHTML = items.slice(0, 6).map(p => 
      `<img src="${p.image}" alt="${escapeHtml(p.title)}"/>`
    ).join('');
    
    // List — every row is clickable to jump to that step. The × remove
    // button is hidden by default; clicking 'Niečo upraviť' toggles
    // editMode which reveals the × on every row.
    const list = stepEl.querySelector('[data-summary-list]');
    list.dataset.editMode = state.editMode ? '1' : '0';
    const roleLabels = { drink: 'Nápoj', deli: 'Delikatesa', accessories: 'Doplnok', pack: 'Balenie' };
    const roleStep = { drink: 0, deli: 1, accessories: 2, pack: 3, wish: 4 };
    list.innerHTML = items.map(p => {
      const stepIdx = roleStep[p._role] ?? 0;
      return `
        <div class="gc-fb-list-row" data-row-role="${p._role}" data-row-id="${p.id || p.variantId}" data-action="goto" data-target="${stepIdx}" role="button" tabindex="0">
          <img src="${p.image}" alt=""/>
          <div class="gc-fb-list-meta">
            ${escapeHtml(p.title)}
            <small>${roleLabels[p._role] || ''}</small>
          </div>
          <div class="gc-fb-list-price">${formatPrice(p.price)}</div>
          <button type="button" class="gc-fb-list-remove" data-action="remove-item" data-role="${p._role}" data-id="${p.id || p.variantId}" aria-label="Odstrániť ${escapeHtml(p.title)}">×</button>
        </div>
      `;
    }).join('');

    // Wish row (also clickable → jumps to step 4)
    const wish = state.selections.wish;
    if (wish.text || wish.to) {
      list.insertAdjacentHTML('beforeend', `
        <div class="gc-fb-list-row" data-row-role="wish" data-action="goto" data-target="4" role="button" tabindex="0">
          <div class="gc-fb-list-card-icon" aria-hidden="true"></div>
          <div class="gc-fb-list-meta">
            Kartička s prianím
            <small>${escapeHtml(wish.to || '')} ${wish.from ? '· od ' + escapeHtml(wish.from) : ''}</small>
          </div>
          <div class="gc-fb-list-price">Zdarma</div>
          <button type="button" class="gc-fb-list-remove" data-action="remove-item" data-role="wish" aria-label="Odstrániť kartičku">×</button>
        </div>
      `);
    }

    // Update the "Niečo upraviť" button label to reflect current mode
    const backBtn = stepEl.querySelector('.gc-fb-back');
    if (backBtn) {
      backBtn.textContent = state.editMode ? 'Hotovo s úpravami' : 'Niečo upraviť';
      backBtn.dataset.action = 'toggle-edit-mode';
      backBtn.removeAttribute('data-target');
    }
  }

  // Remove a single picked item from state and re-render the summary.
  // Works for drink / deli / accessories (selection arrays), pack (single
  // selection), and wish (the gift card).
  function removeItem(role, id) {
    if (role === 'pack') {
      state.selections.pack = null;
    } else if (role === 'wish') {
      state.selections.wish = { to: '', from: '', text: '', template: 'minimal' };
    } else if (Array.isArray(state.selections[role])) {
      state.selections[role] = state.selections[role].filter((p) => String(p.id || p.variantId) !== String(id));
    }
    saveState();
    renderSummary();
    renderProgress();
  }

  // ---------- Drawer ----------
  // Persistent right-rail (desktop) / bottom-sheet (mobile) summary
  // of every picked item. Tappable thumbs jump to that role's step.
  function renderDrawer() {
    const drawer = root.querySelector('[data-drawer]');
    if (!drawer) return;
    // Hide the drawer on the summary step — that step is itself a
    // gift overview so the drawer would be a redundant second summary.
    drawer.hidden = STEPS[state.step] === 'summary';
    if (drawer.hidden) return;
    const items = getAllItems();
    const total = getTotalPrice();
    const totalText = formatPrice(total);

    const countLabel = items.length === 0 ? '0 položiek'
      : items.length === 1 ? '1 položka'
      : items.length >= 2 && items.length <= 4 ? `${items.length} položky`
      : `${items.length} položiek`;

    drawer.querySelector('[data-drawer-count]').textContent = countLabel;
    drawer.querySelector('[data-drawer-total]').textContent = totalText;
    drawer.querySelector('[data-drawer-total-amount]').textContent = totalText;

    const list = drawer.querySelector('[data-drawer-list]');
    const cta = drawer.querySelector('[data-drawer-cta]');
    const roleStep = { drink: 0, deli: 1, accessories: 2, pack: 3, wish: 4 };
    const roleLabels = { drink: 'Nápoj', deli: 'Delikatesa', accessories: 'Doplnok', pack: 'Balenie' };

    if (items.length === 0) {
      list.innerHTML = '<p class="gc-drawer-empty">Zatiaľ ste nevybrali nič. Začnite na prvom kroku.</p>';
      cta.disabled = true;
      cta.textContent = 'Pridať do košíka →';
      return;
    }

    list.innerHTML = items.map((p, i) => {
      const stepIdx = roleStep[p._role] ?? 0;
      return `
        <button type="button" class="gc-drawer-item" data-action="goto" data-target="${stepIdx}" data-row-role="${p._role}">
          <span class="gc-drawer-num">${i + 1}</span>
          <img class="gc-drawer-thumb" src="${p.image}" alt="" loading="lazy"/>
          <div class="gc-drawer-meta">
            <span class="gc-drawer-title">${escapeHtml(p.title)}</span>
            <small class="gc-drawer-role">${roleLabels[p._role] || ''}</small>
          </div>
          <span class="gc-drawer-price">${formatPrice(p.price)}</span>
        </button>
      `;
    }).join('');

    // Optional wish row (the gift card is also part of the bundle)
    const wish = state.selections.wish;
    if (wish && (wish.text || wish.to)) {
      list.insertAdjacentHTML('beforeend', `
        <button type="button" class="gc-drawer-item" data-action="goto" data-target="4" data-row-role="wish">
          <span class="gc-drawer-num">+</span>
          <span class="gc-drawer-thumb gc-drawer-thumb-card" aria-hidden="true">✉</span>
          <div class="gc-drawer-meta">
            <span class="gc-drawer-title">Kartička s prianím</span>
            <small class="gc-drawer-role">${escapeHtml(wish.to || '—')}</small>
          </div>
          <span class="gc-drawer-price">Zdarma</span>
        </button>
      `);
    }

    // Enable CTA when at least one product + packaging is set (matches addToCart's
    // requirement). Drawer CTA is the same `data-action="add-to-cart"` that the
    // summary uses, so the existing flow handles cart-add.
    cta.disabled = items.length === 0 || !state.selections.pack;
    cta.textContent = state.selections.pack
      ? `Pridať do košíka — ${totalText}`
      : 'Najprv vyberte balenie';
  }

  // Toggle drawer on mobile (the pill expands the panel).
  function bindDrawerToggle() {
    const drawer = root.querySelector('[data-drawer]');
    if (!drawer) return;
    drawer.querySelectorAll('[data-drawer-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = drawer.dataset.open === '1';
        drawer.dataset.open = open ? '0' : '1';
        const pill = drawer.querySelector('[data-drawer-toggle]');
        if (pill && pill.matches('.gc-drawer-pill')) {
          pill.setAttribute('aria-expanded', String(!open));
        }
      });
    });
  }

  // ---------- Footer ----------
  function renderFooter() {
    const foot = root.querySelector('[data-foot]');
    const stepName = STEPS[state.step];
    
    if (stepName === 'summary') {
      foot.hidden = true;
      return;
    }
    foot.hidden = false;
    
    const meta = foot.querySelector('[data-foot-meta]');
    const cta = foot.querySelector('.gc-foot-cta');
    const skip = foot.querySelector('.gc-foot-skip');
    
    let count = 0, canProceed = false, isOptional = true;
    
    if (stepName === 'drink') {
      count = state.selections.drink.length;
      // Drink is optional — customer can build a gift around just deli
      // items (e.g. only chocolate) without picking any wine/spirit.
      canProceed = true;
      isOptional = true;
    } else if (stepName === 'deli') {
      count = state.selections.deli.length;
      canProceed = true;
    } else if (stepName === 'accessories') {
      count = state.selections.accessories.length;
      canProceed = true;
    } else if (stepName === 'pack') {
      canProceed = !!state.selections.pack;
      count = state.selections.pack ? 1 : 0;
      isOptional = false;
    } else if (stepName === 'wish') {
      canProceed = true;
    }
    
    meta.textContent = stepName === 'wish' ? 'Voliteľné' : `${count} ${count === 1 ? 'vybrané' : count >= 2 && count <= 4 ? 'vybrané' : 'vybraných'}`;
    cta.disabled = !canProceed;
    cta.querySelector('.gc-foot-cta-label').textContent = state.step === STEPS.length - 2 ? 'Zhrnutie' : 'Pokračovať';
    skip.style.visibility = isOptional ? 'visible' : 'hidden';
  }

  // ---------- Navigation ----------
  function goTo(stepIdx) {
    state.step = Math.max(0, Math.min(STEPS.length - 1, stepIdx));
    if ((state.maxStepReached || 0) < state.step) state.maxStepReached = state.step;
    saveState();
    renderStep();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ---------- Add to cart ----------
  async function addToCart() {
    const items = getAllItems();
    if (!items.length) return;
    
    const bundleId = 'gift-' + Date.now().toString(36);
    const bundleLabel = state.selections.pack 
      ? `Darčekový set — ${state.selections.pack.title}`
      : 'Darčekový set';
    
    // Build line items array
    const lineItems = items.map((p, idx) => ({
      id: p.variantId,
      quantity: 1,
      properties: {
        '_bundle_id': bundleId,
        '_bundle_label': bundleLabel,
        '_bundle_role': p._role,
        '_bundle_position': String(idx + 1),
        // Attach the VAT-rate line-item property so configured gift-set lines
        // show "DPH X %" at checkout like every other add-to-cart path. Mirrors
        // snippets/vat-rate.liquid (dph-5 -> 5 %, dph-19 -> 19 %, default 23 %).
        // Tags are already lowercased in normalizeProduct().
        'DPH': ((p.tags || []).includes('dph-5') ? '5 %' : (p.tags || []).includes('dph-19') ? '19 %' : '23 %'),
        ...(p._role === 'pack' && state.selections.wish.text ? {
          'Prianie pre': state.selections.wish.to || '',
          'Prianie od': state.selections.wish.from || '',
          'Text prianie': state.selections.wish.text || ''
        } : {})
      }
    }));
    
    const cta = root.querySelector('.gc-fb-cta');
    cta.disabled = true;
    cta.textContent = 'Pridávam…';
    
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: lineItems })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Shopify's bulk-add is atomic — if any variant is sold out
        // between page load and submit, the WHOLE bundle is rejected.
        // Try to surface which item failed so the customer can swap it.
        const culpritId = err && (err.id || (err.errors && err.errors.id));
        if (culpritId) {
          const culpritItem = items.find((i) => String(i.variantId) === String(culpritId));
          const title = culpritItem ? culpritItem.title : 'jedna z položiek';
          throw new Error(`„${title}" už nie je dostupná. Vráťte sa späť a vyberte inú položku.`);
        }
        throw new Error(err.description || err.message || 'Chyba pri pridávaní do košíka');
      }

      // Success — clear state, redirect
      sessionStorage.removeItem(STORAGE_KEY);
      window.location.href = '/cart';

    } catch (e) {
      console.error('[GC] cart add failed', e);
      cta.disabled = false;
      cta.innerHTML = `Skúsiť znova — <span>${formatPrice(getTotalPrice())}</span>`;
      alert('Nepodarilo sa pridať do košíka: ' + e.message);
    }
  }

  // ---------- Event delegation ----------
  root.addEventListener('click', (e) => {
    const action = e.target.closest('[data-action]');
    if (action) {
      const a = action.dataset.action;
      if (a === 'next') goTo(state.step + 1);
      if (a === 'skip') goTo(state.step + 1);
      if (a === 'goto') goTo(parseInt(action.dataset.target, 10));
      if (a === 'add-to-cart') addToCart();
      if (a === 'reset-config') {
        const items = getAllItems();
        const hasContent = items.length > 0
          || state.selections.wish.text
          || state.selections.wish.to;
        const doReset = () => {
          sessionStorage.removeItem(STORAGE_KEY);
          state = initialState();
          renderStep();
        };
        if (!hasContent) { doReset(); return; }
        showConfirm({
          title: 'Začať odznova?',
          body: 'Všetky vybrané položky a údaje na kartičke sa stratia.',
          confirm: 'Áno, vymazať',
          cancel: 'Späť',
        }).then((ok) => { if (ok) doReset(); });
      }
      if (a === 'toggle-edit-mode') {
        state.editMode = !state.editMode;
        saveState();
        renderSummary();
      }
      if (a === 'remove-item') {
        removeItem(action.dataset.role, action.dataset.id);
        e.stopPropagation();
        return;
      }
    }

    const jump = e.target.closest('[data-step-jump]');
    if (jump) {
      const target = parseInt(jump.dataset.stepJump, 10);
      // From the summary view (step 5) any earlier step is reachable;
      // from earlier steps we still allow jumping forward to any step
      // the user has already advanced past, so the dots act as both
      // breadcrumbs and shortcuts.
      const maxReachable = Math.max(state.step, state.maxStepReached || 0);
      if (target <= maxReachable) goTo(target);
    }
    
    const seg = e.target.closest('[data-drink-type]');
    if (seg) {
      state.drinkType = seg.dataset.drinkType;
      // Reset drink selection if switching type
      state.selections.drink = [];
      saveState();
      root.querySelectorAll('[data-drink-type]').forEach(s => {
        s.classList.toggle('gc-seg-on', s === seg);
        s.setAttribute('aria-selected', s === seg ? 'true' : 'false');
      });
      renderProductStep('drink', state.drinkType === 'vino' ? 'vino' : 'destilaty-a-liehoviny', 0);
      renderFooter();
    }
  });

  // ---------- Helpers ----------
  function formatPrice(n) {
    return new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(n);
  }
  function capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // In-page confirm modal — replaces the native browser confirm() which
  // shows a Chrome-branded dialog and breaks the editorial look. Returns
  // a Promise<boolean>. Closes on overlay click, Esc, or any button.
  function showConfirm({ title, body, confirm, cancel }) {
    return new Promise((resolve) => {
      const ov = document.createElement('div');
      ov.className = 'gc-modal-ov';
      ov.innerHTML = `
        <div class="gc-modal" role="dialog" aria-modal="true" aria-labelledby="gc-modal-t">
          <h3 class="gc-modal-t" id="gc-modal-t">${escapeHtml(title)}</h3>
          <p class="gc-modal-b">${escapeHtml(body)}</p>
          <div class="gc-modal-actions">
            <button type="button" class="gc-modal-btn gc-modal-btn-ghost" data-modal="cancel">${escapeHtml(cancel)}</button>
            <button type="button" class="gc-modal-btn gc-modal-btn-primary" data-modal="confirm">${escapeHtml(confirm)}</button>
          </div>
        </div>
      `;
      const close = (val) => {
        document.removeEventListener('keydown', onKey);
        ov.classList.add('gc-modal-ov-closing');
        setTimeout(() => ov.remove(), 160);
        resolve(val);
      };
      const onKey = (e) => {
        if (e.key === 'Escape') close(false);
        if (e.key === 'Enter') close(true);
      };
      ov.addEventListener('click', (e) => {
        if (e.target === ov) return close(false);
        const btn = e.target.closest('[data-modal]');
        if (btn) close(btn.dataset.modal === 'confirm');
      });
      document.addEventListener('keydown', onKey);
      document.body.appendChild(ov);
      requestAnimationFrame(() => ov.classList.add('gc-modal-ov-open'));
      const primary = ov.querySelector('[data-modal="confirm"]');
      if (primary) primary.focus();
    });
  }

  // ---------- Boot ----------
  bindDrawerToggle();
  renderStep();
})();
